import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Box, IconButton, Slider, Typography, Tooltip } from '@mui/material';
import { ZoomIn, ZoomOut, RestartAlt } from '@mui/icons-material';
import { useTheme } from '@emotion/react';

// The value axes a chart may define, whatever it calls them: y and y2 on the dual-axis line
// chart, y and y1 on the stacked bars (y1 carries the line series and has to move with y),
// y-axis-1 and y-axis-2 on the multiple-axis chart. Looking them up as fixed pairs missed y2 -
// on a dual-axis chart only the left axis zoomed - and missed y-axis-1 whenever y-axis-2 was
// not drawn.
const VALUE_AXES = ['y', 'y1', 'y2', 'y-axis-1', 'y-axis-2'];

const readAxisLimits = (chart) => {
    const axes = VALUE_AXES
        .map((name) => ({ name, scale: chart?.scales?.[name] }))
        .filter(({ scale }) => scale && Number.isFinite(scale.min) && Number.isFinite(scale.max))
        .map(({ name, scale }) => ({
            name,
            min: scale.min,
            max: scale.max,
            logarithmic: scale.type === 'logarithmic',
            // Bounds the chart sets itself (the stacked bars fix their max): a reset has to
            // put these back rather than drop them.
            optionMin: chart.options?.scales?.[name]?.min,
            optionMax: chart.options?.scales?.[name]?.max,
        }));
    return axes.length > 0 ? axes : null;
};

// "Nice number" rounding (standard Heckbert algorithm) so zoomed axis boundaries land on
// clean intervals (e.g. 25, 50) instead of arbitrary values (e.g. 23.7, 48.3).
const niceNum = (range, round) => {
    if (!isFinite(range) || range <= 0) return range;
    const exponent = Math.floor(Math.log10(range));
    const fraction = range / Math.pow(10, exponent);
    let niceFraction;
    if (round) {
        if (fraction < 1.5) niceFraction = 1;
        else if (fraction < 3) niceFraction = 2;
        else if (fraction < 7) niceFraction = 5;
        else niceFraction = 10;
    } else {
        if (fraction <= 1) niceFraction = 1;
        else if (fraction <= 2) niceFraction = 2;
        else if (fraction <= 5) niceFraction = 5;
        else niceFraction = 10;
    }
    return niceFraction * Math.pow(10, exponent);
};

const niceStep = (min, max, numTicks = 5) => niceNum(niceNum(max - min, false) / (numTicks - 1), true);

const niceAxisBounds = (min, max, numTicks = 5) => {
    if (min === max) return { min, max };
    const step = niceStep(min, max, numTicks);
    if (!step) return { min, max };
    return { min: Math.floor(min / step) * step, max: Math.ceil(max / step) * step };
};

// A log axis is zoomed and panned in powers of ten: in linear terms shrinking its range around
// the centre drives the minimum to zero or below, where a logarithmic scale cannot go.
const project = (axis, value) => (axis.logarithmic ? Math.log10(value) : value);
const unproject = (axis, value) => (axis.logarithmic ? Math.pow(10, value) : value);

// A logarithmic axis labels its bounds whatever they are (includeBounds does not apply to it),
// so they are rounded to two significant figures instead: 17,901,543 becomes 17,000,000.
const roundLogBounds = ({ min, max }) => {
    const digits = (value) => Math.pow(10, Math.floor(Math.log10(value)) - 1);
    return { min: Math.floor(min / digits(min)) * digits(min), max: Math.ceil(max / digits(max)) * digits(max) };
};

// Bounds showing `fraction` of the axis's original range, around the centre of what is visible now.
const zoomBounds = (axis, current, fraction) => {
    const range = (project(axis, axis.max) - project(axis, axis.min)) * fraction;
    const center = (project(axis, current.min) + project(axis, current.max)) / 2;
    const min = center - range / 2;
    const max = center + range / 2;
    if (axis.logarithmic) return roundLogBounds({ min: unproject(axis, min), max: unproject(axis, max) });
    return niceAxisBounds(min, max);
};

// The same bounds moved by `fraction` of the visible range.
const panBounds = (axis, start, fraction) => {
    const min = project(axis, start.min);
    const max = project(axis, start.max);
    const shift = (max - min) * fraction;
    return { min: unproject(axis, min + shift), max: unproject(axis, max + shift) };
};

// Panning moves the bounds by however far the mouse travelled, which is how 23.7 and 48.3 end
// up at the ends of the axis. On release the minimum goes to the nearest multiple of the tick
// step and the maximum keeps the same distance from it: the zoom level stays what the slider
// says, and repeated pans neither grow nor shrink the range.
const snapBounds = (axis, bounds) => {
    if (axis.logarithmic) return roundLogBounds(bounds);
    const step = niceStep(bounds.min, bounds.max);
    if (!step) return bounds;
    const min = Math.round(bounds.min / step) * step;
    return { min, max: min + (bounds.max - bounds.min) };
};

const ZoomControls = ({ chartRef, data }) => {
    const [zoomLevel, setZoomLevel] = useState(0); // Start at 0% (maximum zoom out)
    const [originalAxes, setOriginalAxes] = useState(null); // Value axes and their unzoomed limits
    const currentZoomRef = useRef(1); // Track current absolute zoom factor (1x .. 4x)
    const savedStateRef = useRef(null); // Store zoom/pan state to reapply after re-renders
    const theme = useTheme();

    // Convert slider value (0-100) to fraction of original range to show (1.0 = 100%, 0.25 = 25%)
    // This value is used multiplicatively by the plugin (smaller = more zoom-in)
    const getZoomFactor = (sliderValue) => {
        const normalized = Math.max(0, Math.min(1, sliderValue / 100)); // 0..1
        // Map 0% -> 1.0 (show 100% of range), 100% -> 0.25 (show 25% = 4x zoom)
        const fraction = 1 - (0.75 * normalized);
        return fraction; // 1.0 .. 0.25
    };

    // The zoom level is passed in by callers that have just changed it: the one in state is
    // still the previous value until the next render.
    const saveCurrentState = useCallback((chart, axes = originalAxes, level = zoomLevel) => {
        if (!chart || !chart.scales || !axes) return;

        const state = { zoomLevel: level, scales: {} };

        axes.forEach(({ name }) => {
            if (chart.scales[name]) {
                state.scales[name] = { min: chart.scales[name].min, max: chart.scales[name].max };
            }
        });

        // Save X-axis zoom state (from chartjs-plugin-zoom)
        if (chart.scales.x) {
            state.scales.x = { min: chart.scales.x.min, max: chart.scales.x.max };
        }

        savedStateRef.current = state;
    }, [zoomLevel, originalAxes]);

    const applyZoom = (zoomValue) => {
        const chart = chartRef?.current;
        if (!chart || !chart.scales) return; // Scales not ready yet

        // The limits are normally captured once the chart has drawn; if the user is quicker than
        // that, capture them now and use them straight away rather than the still-empty state.
        const axes = originalAxes ?? readAxisLimits(chart);
        if (!axes) return;
        if (!originalAxes) setOriginalAxes(axes);

        const scalesOpts = chart.options?.scales || {};
        const fraction = getZoomFactor(zoomValue);

        axes.forEach((axis) => {
            const options = scalesOpts[axis.name];
            if (!options) return;

            if (zoomValue === 0) {
                if (axis.optionMin === undefined) delete options.min; else options.min = axis.optionMin;
                if (axis.optionMax === undefined) delete options.max; else options.max = axis.optionMax;
                return;
            }

            const scale = chart.scales[axis.name];
            const bounds = zoomBounds(axis, { min: scale?.min ?? axis.min, max: scale?.max ?? axis.max }, fraction);
            options.min = bounds.min;
            options.max = bounds.max;
        });

        chart.update('none');
        currentZoomRef.current = fraction;
        saveCurrentState(chart, axes, zoomValue);
    };


    // Store original limits when chart is ready
    useEffect(() => {
        const timer = setTimeout(() => {
            if (chartRef?.current && !originalAxes) {
                const axes = readAxisLimits(chartRef.current);
                if (!axes) return; // Scales not ready yet, will retry on next render
                setOriginalAxes(axes);
                currentZoomRef.current = 1.0; // reset current zoom tracking on init (1.0 = 100% of range)
            }
        }, 200); // Increased delay to ensure chart is fully rendered

        return () => clearTimeout(timer);
    }, [chartRef, originalAxes]);

    // Listen for X-axis zoom events from chartjs-plugin-zoom (drag zoom)
    useEffect(() => {
        if (!chartRef || !chartRef.current || !originalAxes) return;

        const chart = chartRef.current;
        const canvas = chart.canvas;
        if (!canvas) return;

        // Handler for when drag zoom completes
        const handleMouseUp = () => {
            // Save state after a short delay to ensure zoom has been applied
            setTimeout(() => {
                if (chart.scales) {
                    saveCurrentState(chart);
                }
            }, 50);
        };

        // Listen for mouseup on the canvas (drag zoom completes on mouseup)
        canvas.addEventListener('mouseup', handleMouseUp);

        return () => {
            canvas.removeEventListener('mouseup', handleMouseUp);
        };
    }, [chartRef, originalAxes, saveCurrentState]);

    // Restore zoom/pan state after chart re-renders (e.g., when data labels toggle)
    useEffect(() => {
        if (!chartRef || !chartRef.current || !savedStateRef.current || !originalAxes) return;

        const chart = chartRef.current;
        const savedState = savedStateRef.current;

        // Don't restore if zoom level is 0 (fully reset state)
        if (savedState.zoomLevel === 0) return;

        // Wait for chart to be fully rendered before restoring state
        const timer = setTimeout(() => {
            if (!chart.scales) return;

            const scalesOpts = chart.options?.scales || {};

            // Restore all axis zoom/pan state (including X-axis)
            Object.keys(savedState.scales).forEach(axisName => {
                if (scalesOpts[axisName] && chart.scales[axisName]) {
                    scalesOpts[axisName].min = savedState.scales[axisName].min;
                    scalesOpts[axisName].max = savedState.scales[axisName].max;
                }
            });

            // Restore zoom level state
            setZoomLevel(savedState.zoomLevel);

            chart.update('none');
        }, 100);

        return () => clearTimeout(timer);
    }, [data, chartRef, originalAxes]); // Re-run when data changes (which includes showDataLabels changes)

    // Add panning functionality when zoomed in
    useEffect(() => {
        if (!chartRef || !chartRef.current || zoomLevel === 0 || !originalAxes) return;

        const chart = chartRef.current;
        const canvas = chart.canvas;
        if (!canvas) return;

        let isPanning = false;
        let startY = 0;
        let startScales = {};

        const handleMouseDown = (e) => {
            if (zoomLevel > 0) { // Only allow panning when zoomed in
                isPanning = true;
                startY = e.clientY;

                // Store current scale limits
                startScales = {};
                originalAxes.forEach(({ name }) => {
                    if (chart.scales[name]) {
                        startScales[name] = { min: chart.scales[name].min, max: chart.scales[name].max };
                    }
                });

                canvas.style.cursor = 'grabbing';
                e.preventDefault();
            }
        };

        const handleMouseMove = (e) => {
            if (!isPanning) return;

            const scalesOpts = chart.options?.scales || {};
            // Calculate pan amount based on pixel movement
            const panFactor = (e.clientY - startY) / canvas.height;

            originalAxes.forEach((axis) => {
                if (!startScales[axis.name] || !scalesOpts[axis.name]) return;
                const bounds = panBounds(axis, startScales[axis.name], panFactor);
                scalesOpts[axis.name].min = bounds.min;
                scalesOpts[axis.name].max = bounds.max;
            });

            chart.update('none');
        };

        const finishPanning = (cursor) => {
            if (!isPanning) return;
            isPanning = false;
            canvas.style.cursor = cursor;

            const scalesOpts = chart.options?.scales || {};
            originalAxes.forEach((axis) => {
                const options = scalesOpts[axis.name];
                if (!options || !Number.isFinite(options.min) || !Number.isFinite(options.max)) return;
                const bounds = snapBounds(axis, { min: options.min, max: options.max });
                options.min = bounds.min;
                options.max = bounds.max;
            });
            chart.update('none');

            // Save state after panning is complete
            saveCurrentState(chart);
        };

        const handleMouseUp = () => finishPanning(zoomLevel > 0 ? 'grab' : 'default');
        const handleMouseLeave = () => finishPanning('default');

        // Set cursor style based on zoom level
        canvas.style.cursor = zoomLevel > 0 ? 'grab' : 'default';

        canvas.addEventListener('mousedown', handleMouseDown);
        canvas.addEventListener('mousemove', handleMouseMove);
        canvas.addEventListener('mouseup', handleMouseUp);
        canvas.addEventListener('mouseleave', handleMouseLeave);

        return () => {
            canvas.removeEventListener('mousedown', handleMouseDown);
            canvas.removeEventListener('mousemove', handleMouseMove);
            canvas.removeEventListener('mouseup', handleMouseUp);
            canvas.removeEventListener('mouseleave', handleMouseLeave);
            canvas.style.cursor = 'default';
        };
    }, [chartRef, zoomLevel, originalAxes, saveCurrentState]);

    const handleZoomChange = (event, newValue, activeThumb) => {
        setZoomLevel(newValue);
        // Only update visual slider position, don't zoom chart yet
    };

    const handleZoomChangeCommitted = (event, newValue) => {
        // This fires when user releases the slider - actually zoom the chart
        applyZoom(newValue);
    };

    const handleZoomIn = () => {
        const newZoom = Math.min(zoomLevel + 10, 100);
        setZoomLevel(newZoom);
        applyZoom(newZoom);
    };

    const handleZoomOut = () => {
        const newZoom = Math.max(zoomLevel - 10, 0);
        setZoomLevel(newZoom);
        applyZoom(newZoom);
    };

    const handleReset = () => {
        setZoomLevel(0);
        applyZoom(0);
        // Also reset X-axis zoom
        if (chartRef && chartRef.current) {
            const chart = chartRef.current;
            chart.resetZoom('x');
            // Save the reset state after a short delay to ensure X-axis reset is applied
            setTimeout(() => {
                saveCurrentState(chart, originalAxes, 0);
            }, 50);
        }
    };

    return (
        <Box
            sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 0.25,
                py: 0.125,
                px: 0.5,
                borderTop: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.12)'}`,
                backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.02)' : '#e5ecf4',
                height: '18px',
                width: '100%',
            }}
        >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
                <Typography
                    variant="body2"
                    sx={{
                        minWidth: '28px',
                        textAlign: 'center',
                        color: theme.palette.primary.fontColor,
                        fontSize: '0.625rem',
                        fontWeight: 500,
                    }}
                >
                    Zoom Y axis
                </Typography>
                <Tooltip
                    title="Zoom out Y-axis"
                    placement="top"
                    arrow
                >
                    <IconButton
                        onClick={handleZoomOut}
                        disabled={zoomLevel <= 0}
                        size="small"
                        sx={{
                            color: theme.palette.primary.fontColor,
                            width: 16,
                            height: 16,
                            padding: 0,
                            '&:hover': {
                                backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.04)',
                            }
                        }}
                    >
                        <ZoomOut sx={{ fontSize: 10 }} />
                    </IconButton>
                </Tooltip>
            </Box>

            <Slider
                value={zoomLevel}
                onChange={handleZoomChange}
                onChangeCommitted={handleZoomChangeCommitted}
                min={0}
                max={100}
                step={5}
                sx={{
                    flex: 1,
                    mx: 0.5,
                    maxWidth: '100%',
                    height: 'auto',
                    '& .MuiSlider-thumb': {
                        width: 8,
                        height: 8,
                        color: theme.palette.primary.main,
                        '&:hover, &.Mui-focusVisible': {
                            boxShadow: `0px 0px 0px 4px ${theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.16)' : 'rgba(0,0,0,0.04)'}`,
                        },
                    },
                    '& .MuiSlider-track': {
                        color: theme.palette.primary.main,
                        // height: 1,
                    },
                    '& .MuiSlider-rail': {
                        color: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.23)' : 'rgba(0,0,0,0.23)',
                        // height: 1,
                    },
                    '& .MuiSlider-valueLabel': {
                        fontSize: '0.625rem',
                    },
                }}
                valueLabelDisplay="auto"
                valueLabelFormat={(value) => `${value}%`}
            />

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
                <Tooltip
                    title="Zoom in Y-axis"
                    placement="top"
                    arrow
                >
                    <IconButton
                        onClick={handleZoomIn}
                        disabled={zoomLevel >= 100}
                        size="small"
                        sx={{
                            color: theme.palette.primary.fontColor,
                            width: 16,
                            height: 16,
                            padding: 0,
                            '&:hover': {
                                backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.04)',
                            }
                        }}
                    >
                        <ZoomIn sx={{ fontSize: 10 }} />
                    </IconButton>
                </Tooltip>

                <Box sx={{ width: '1px', height: '12px', backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.12)', mx: 0.125 }} />

                <Tooltip
                    title="Reset both X and Y axis zoom to original scale"
                    placement="top"
                    arrow
                >
                    <IconButton
                        onClick={handleReset}
                        size="small"
                        sx={{
                            color: theme.palette.primary.fontColor,
                            width: 16,
                            height: 16,
                            padding: 0,
                            '&:hover': {
                                backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.04)',
                            }
                        }}
                    >
                        <RestartAlt sx={{ fontSize: 10 }} />
                    </IconButton>
                </Tooltip>
            </Box>
        </Box>
    );
};

export default ZoomControls;
